import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { IoIosSend } from "react-icons/io";
import { get, capitalize, isEmpty } from "lodash";
import clsx from "clsx";
import { formatPrice } from "../../../../../helpers/helpers";
import AutoResizeTextarea from "../../../../../components/common/AutoResizeTextarea";
import { isMobile } from "react-device-detect";

const DELETED_COMMENT_TEXT = "Comment deleted";

/**
 * Comment component displays a single comment with user information and content
 * @param {string} props.id - Unique identifier for the comment element
 * @param {Object} props.comment - Comment data
 * @param {number} props.maxLines - Number of lines to show before clamping (default: 3)
 * @param {boolean} props.showSeeMore - Whether to show the "See more" button (default: true)
 * @param {number} props.depth - Depth of the comment in the reply hierarchy (default: 0)
 * @param {function} props.onSubmitReply - Function to submit reply via desktop
 * @param {function} props.onDelete - Function to delete the comment
 * @param {function} props.onReply - Function to reply to the comment
 * @param {boolean} props.isExpanded - Whether the replies are expanded
 * @param {boolean} props.enableReplies - Whether to enable reply functionality (default: true)
 * @param {boolean} props.enableNestedReplies - Whether to show nested replies (default: true)
 * @param {boolean} props.showReplyButton - Whether to show reply button (default: true)
 * @param {boolean} props.showViewMoreButton - Whether to show view more replies button (default: true)
 * @param {boolean} props.showDeleteButton - Whether to show delete button (default: false)
 * @param {boolean} props.isReplyingTo - Whether the comment is being replied to (default: false)
 * @param {string} props.replyingTo - The actual comment id or parent id being replied to
 * @param {string} props.currentUserId - ID of the current user
 * @param {boolean} props.isLastReply - Whether the comment is the last reply in the thread (default: false)
 * @param {boolean} props.hasParent - Whether the comment has a parent
 * @param {boolean} props.hasChildren - Whether the comment has children
 * @param {string} props.tag - Tag for the comment
 * @param {string} props.tagVote - Tag vote for the comment
 */
const Comment = ({
    id,
    comment,
    maxLines = 3,
    showSeeMore = true,
    depth = 0,
    onSubmitReply,
    onDelete,
    onReply,
    isExpanded = false,
    enableReplies = true,
    enableNestedReplies = true,
    showReplyButton = true,
    showViewMoreButton = true,
    showDeleteButton = false,
    isReplyingTo = false,
    replyingTo,
    currentUserId = null,
    isLastReply = false,
    hasParent = false,
    hasChildren = false,
    tag = null,
    tagVote = null,
}) => {
    const navigate = useNavigate();
    const [isContentExpanded, setIsContentExpanded] = useState(false);
    const [shouldShowMore, setShouldShowMore] = useState(false);
    const [showAllReplies, setShowAllReplies] = useState(isExpanded);
    const [replyText, setReplyText] = useState("");
    const [isReplying, setIsReplying] = useState(false);
    const contentRef = useRef(null);
    const replyInputRef = useRef(null);
    const userGuess = get(comment, "User.UserGuesses[0].prediction", "");
    const userGuessPrice = get(comment, "User.UserGuesses[0].guess", 0);
    const replies = get(comment, "replies", []);
    const isCurrentUserComment = currentUserId === get(comment, "User.id");
    const isDeleted = comment.content === DELETED_COMMENT_TEXT;
    const tagNameData = get(comment, "Tag.name", null);
    const tagVoteData = get(comment, "TagVote", null);

    useEffect(() => {
        if (contentRef.current) {
            const element = contentRef.current;
            setShouldShowMore(element.scrollHeight > element.clientHeight);
        }
    }, [comment.content]);

    useEffect(() => {
        if (isReplying && replyInputRef.current) {
            replyInputRef.current.focus();
        }
    }, [isReplying]);

    const getUserGuessColor = (guess) => {
        switch (guess.toLowerCase()) {
            case "higher":
                return "tw-text-[#7CC836]";
            case "lower":
                return "tw-text-[#EA4949]";
            case "exact":
                return "tw-text-[#13C5FC]";
            default:
                return "tw-text-gray-500";
        }
    };

    const handleUserClick = () => {
        if (comment.User?.id) {
            navigate(`/social/profile/${comment.User.id}`);
        }
    };

    const toggleExpand = () => {
        setIsContentExpanded(!isContentExpanded);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpand();
        }
    };

    // Submit reply via desktop
    const handleReplySubmit = () => {
        if (replyText.trim() && onSubmitReply) {
            onSubmitReply({ content: replyText, parentId: comment.id });
            setReplyText("");
            setIsReplying(false);
        }
    };

    const handleReplyClick = () => {
        if (isMobile) {
            // On mobile, pass the comment ID and whether we're starting or canceling a reply
            onReply(comment.id, isReplyingTo ? null : "");
        } else {
            // On desktop, toggle the reply input field
            setIsReplying(!isReplying);
            if (isReplying) {
                // If we're canceling, notify the parent
                onReply(comment.id, null);
            } else {
                // If we're starting a reply, notify the parent
                onReply(comment.id, "");
            }
        }
    };

    const renderReplies = () => {
        if (!replies || replies.length === 0 || !enableReplies) return null;

        // Check if any reply has nested replies
        const hasNestedReplies = replies.some((reply) => reply.replies && reply.replies.length > 0);
        const isSingleReplyWithoutNestedReplies = replies.length === 1 && !hasNestedReplies;

        if (enableNestedReplies) {
            if (!showAllReplies && replies.length > 0) {
                // Show only the first reply and the 'View X more replies...' button
                return (
                    <div className="tw-relative tw-ml-6">
                        {/* Vertical line for the reply group, connecting all siblings */}
                        <div
                            className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-[2px] tw-bg-gray-300"
                            style={{ zIndex: 0 }}
                        />
                        <div>
                            <div className="tw-relative">
                                <Comment
                                    id={`comment-${replies[0].id}`}
                                    comment={replies[0]}
                                    depth={depth + 1}
                                    onReply={onReply}
                                    isExpanded={false}
                                    enableReplies={enableNestedReplies}
                                    enableNestedReplies={false}
                                    showReplyButton={showReplyButton}
                                    showViewMoreButton={showViewMoreButton}
                                    showDeleteButton={true}
                                    currentUserId={currentUserId}
                                    onSubmitReply={onSubmitReply}
                                    onDelete={onDelete}
                                    replyingTo={replyingTo}
                                    isReplyingTo={replyingTo === comment.id}
                                    isLastReply={replies.length === 1}
                                    hasParent={true}
                                    hasChildren={replies[0].replies && replies[0].replies.length > 0}
                                />
                                {/* For the first reply, cover the vertical line below the avatar if it's the only reply */}
                                {isSingleReplyWithoutNestedReplies && (
                                    <div
                                        className="tw-absolute tw-left-0 tw-w-[2px] tw-bg-white"
                                        style={{ zIndex: 2, top: `calc(${avatarCenter}px)`, bottom: 0 }}
                                    />
                                )}
                            </div>
                            {/* Show view more button if there are multiple replies or if the single reply has nested replies */}
                            {showViewMoreButton && (replies.length > 1 || hasNestedReplies) && (
                                <div
                                    className="tw-pl-4 tw-mt-1 tw-text-[13px] tw-text-[#65676B] hover:tw-underline tw-cursor-pointer tw-font-bold"
                                    onClick={() => setShowAllReplies(true)}
                                >
                                    {replies.length > 1
                                        ? `View ${replies.length - 1} more ${replies.length === 2 ? "reply" : "replies"}...`
                                        : "View replies..."}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
            // Show all replies if showAllReplies is true
            return (
                <div className="tw-relative tw-ml-6">
                    {/* Vertical line for the reply group, connecting all siblings */}
                    <div
                        className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-[2px] tw-bg-gray-300"
                        style={{ zIndex: 0 }}
                    />
                    <div>
                        {replies.map((reply, idx) => (
                            <div key={reply.id} className="tw-relative">
                                <Comment
                                    id={`comment-${reply.id}`}
                                    comment={reply}
                                    depth={depth + 1}
                                    onReply={onReply}
                                    isExpanded={true}
                                    enableReplies={enableNestedReplies}
                                    enableNestedReplies={true}
                                    showReplyButton={showReplyButton}
                                    showViewMoreButton={showViewMoreButton}
                                    showDeleteButton={true}
                                    currentUserId={currentUserId}
                                    onSubmitReply={onSubmitReply}
                                    onDelete={onDelete}
                                    replyingTo={replyingTo}
                                    isReplyingTo={replyingTo === comment.id}
                                    isLastReply={idx === replies.length - 1}
                                    hasParent={true}
                                    hasChildren={reply.replies && reply.replies.length > 0}
                                />
                                {/* For the last reply, cover the vertical line below the avatar */}
                                {idx === replies.length - 1 && (
                                    <div
                                        className="tw-absolute tw-left-0 tw-w-[2px] tw-bg-white"
                                        style={{ zIndex: 2, top: `calc(${avatarCenter}px)`, bottom: 0 }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        return null;
    };

    // Calculate profile picture size based on nesting depth
    const profileSize = Math.max(32 - depth * 4, 16);
    const profileIconSize = Math.max(16 - depth * 2, 8);
    // Helper to get avatar center offset
    const avatarCenter = profileSize / 2;

    return (
        <div id={id} className={clsx("tw-py-2", { "tw-pl-4": depth > 0 })}>
            <div className="tw-relative tw-flex tw-items-start">
                <div className="tw-relative">
                    {/* Horizontal line if this is a child */}
                    {hasParent && (
                        <div
                            className="tw-absolute"
                            style={{
                                left: "-16px", // adjust as needed for your spacing
                                top: "25%",
                                width: "16px",
                                height: "16px",
                                borderLeft: "2px solid #D1D5DB", // gray-300
                                borderBottom: "2px solid #D1D5DB",
                                borderBottomLeftRadius: "8px",
                                transform: "translateY(-50%)",
                                zIndex: 1,
                                background: "transparent",
                            }}
                        />
                    )}
                    {/* Vertical line if this is a parent */}
                    {hasChildren && (
                        <div
                            className="tw-absolute tw-left-1/2 tw-top-full tw-w-[1.5px] tw-bg-gray-300"
                            style={{ height: "20px", zIndex: 0, transform: "translateX(-50%)" }}
                        />
                    )}
                    <div
                        onClick={handleUserClick}
                        className={clsx(
                            "tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-relative tw-z-10 tw-border tw-border-gray-200",
                            { "tw-cursor-pointer": comment.User?.id }
                        )}
                        style={{ width: profileSize, height: profileSize }}
                    >
                        {comment.avatar ? (
                            <img
                                src={comment.avatar}
                                alt={comment.name}
                                className="tw-w-full tw-h-full tw-object-cover"
                            />
                        ) : (
                            <FaUser
                                className="tw-text-gray-400"
                                style={{ width: profileIconSize, height: profileIconSize }}
                            />
                        )}
                    </div>
                </div>
                <div className="tw-flex-1">
                    <div className="tw-font-noto tw-text-[14px] tw-flex tw-flex-col">
                        <div className="tw-flex tw-items-baseline tw-gap-2 tw-justify-between">
                            <span
                                onClick={handleUserClick}
                                className={clsx("tw-font-bold", {
                                    "tw-cursor-pointer hover:tw-underline": comment.User?.id,
                                })}
                            >
                                {comment?.User?.nickname}
                            </span>
                            {(tagNameData || tagVoteData) && (
                                <span
                                    className={clsx(
                                        "tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-bold",
                                        tagVoteData ? (
                                            tagVoteData.voteType === "agree"
                                                ? "tw-bg-[#E6F7D9] tw-text-[#55A809]"
                                                : "tw-bg-[#FBE6E7] tw-text-[#DB2528]"
                                        ) : (
                                            "tw-bg-[#F0EDFF] tw-text-[#6c47ff]"
                                        )
                                    )}
                                >
                                    #{tag?.name || tagNameData} {tagVoteData ? tagVoteData.voteType === "agree" ? " - Yes" : " - No" : ''}
                                </span>
                            )}
                            {!isEmpty(userGuess) && (
                                <span className={getUserGuessColor(userGuess)}>
                                    {userGuessPrice > 0
                                        ? `Guessed ${formatPrice(userGuessPrice)}`
                                        : `Guessed ${capitalize(userGuess)}`}
                                </span>
                            )}
                        </div>
                        <div className="tw-relative">
                            {isDeleted ? (
                                <p className="tw-text-[#605E5E] tw-mt-1 tw-mb-1 tw-italic">
                                    This comment has been deleted
                                </p>
                            ) : (
                                <p
                                    ref={contentRef}
                                    className={clsx("tw-text-[#605E5E] tw-mt-1 tw-mb-1", {
                                        [`tw-line-clamp-${maxLines}`]: !isContentExpanded,
                                    })}
                                >
                                    {comment.content}
                                </p>
                            )}
                            {!isDeleted && showSeeMore && shouldShowMore && (
                                <button
                                    onClick={toggleExpand}
                                    onKeyDown={handleKeyDown}
                                    className="tw-bg-white tw-text-blue-500 tw-text-sm tw-p-0 tw-mb-2 hover:tw-text-blue-600"
                                    tabIndex={0}
                                >
                                    {isContentExpanded ? "See less" : "See more"}
                                </button>
                            )}
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-4 tw-mt-1">
                            <span className="tw-text-xs tw-text-[#605E5E]">
                                {moment(comment.created_at).fromNow()}
                            </span>
                            {!isDeleted && showReplyButton && enableReplies && (
                                <button
                                    className={clsx(
                                        "tw-bg-white tw-text-[13px] hover:tw-underline tw-cursor-pointer",
                                        isReplying || (isMobile && replyingTo === comment.id)
                                            ? "tw-text-purple-600 tw-font-bold"
                                            : "tw-text-[#65676B]"
                                    )}
                                    onClick={handleReplyClick}
                                >
                                    Reply
                                </button>
                            )}
                            {!isDeleted && showDeleteButton && isCurrentUserComment && (
                                <button
                                    onClick={() => onDelete(comment.id)}
                                    className="tw-bg-white tw-text-xs tw-px-0 tw-text-[#605E5E] hover:tw-text-red-500"
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Reply Input - Only show on desktop */}
                    {!isMobile && isReplying && !isDeleted && (
                        <div className="tw-mt-2 tw-flex tw-items-start tw-gap-2">
                            <AutoResizeTextarea
                                ref={replyInputRef}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                            />
                            <button
                                className="tw-bg-[#F69929] tw-p-2 tw-rounded-full tw-border-[1px] tw-border-solid tw-border-black hover:tw-bg-[#e89355] tw-transition-colors tw-self-end"
                                onClick={handleReplySubmit}
                            >
                                <IoIosSend className="tw-h-[20px] tw-w-[20px] tw-text-black" />
                            </button>
                        </div>
                    )}

                    {/* Render replies using the function */}
                    {renderReplies()}
                </div>
            </div>
        </div>
    );
};

Comment.propTypes = {
    id: PropTypes.string.isRequired,
    comment: PropTypes.shape({
        content: PropTypes.string.isRequired,
        avatar: PropTypes.string,
        name: PropTypes.string.isRequired,
        User: PropTypes.shape({
            id: PropTypes.string.isRequired,
            nickname: PropTypes.string.isRequired,
            UserGuesses: PropTypes.arrayOf(
                PropTypes.shape({
                    prediction: PropTypes.oneOf(["higher", "lower", "exact"]),
                    guess: PropTypes.number,
                })
            ),
        }),
        created_at: PropTypes.string.isRequired,
        TagVote: PropTypes.shape({
            userId: PropTypes.string.isRequired,
            taggableId: PropTypes.string.isRequired,
            voteType: PropTypes.oneOf(["agree", "disagree"]).isRequired,
        }),
    }).isRequired,
    maxLines: PropTypes.number,
    showSeeMore: PropTypes.bool,
    depth: PropTypes.number,
    onSubmitReply: PropTypes.func,
    onDelete: PropTypes.func,
    onReply: PropTypes.func,
    isExpanded: PropTypes.bool,
    enableReplies: PropTypes.bool,
    enableNestedReplies: PropTypes.bool,
    showReplyButton: PropTypes.bool,
    showViewMoreButton: PropTypes.bool,
    showDeleteButton: PropTypes.bool,
    isReplyingTo: PropTypes.bool,
    replyingTo: PropTypes.string,
    currentUserId: PropTypes.string,
    isLastReply: PropTypes.bool,
    hasParent: PropTypes.bool,
    hasChildren: PropTypes.bool,
    tag: PropTypes.string,
    tagVote: PropTypes.oneOf(["agree", "disagree"]),
};

export default Comment;
